import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Avatar,
  Typography,
  message,
  Popconfirm,
  Row,
  Col,
  Image,
} from 'antd';
import {
  DeleteOutlined,
  ReloadOutlined,
  UserOutlined,
  PlayCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import storiesService, { Story } from '../services/storiesService';
import { getImageUrl } from '../config/api';

const { Title, Text } = Typography;

const userName = (u?: { name?: string; username?: string } | string | null): string => {
  if (!u) return 'Unknown';
  if (typeof u === 'string') return u;
  return u.name || u.username || 'Unknown';
};

const Stories: React.FC = () => {
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchStories = useCallback(async () => {
    try {
      setLoading(true);
      const { stories: list, pagination } = await storiesService.getAllStories({ page, limit: pageSize });
      setStories(list);
      setTotal(pagination.totalItems ?? (pagination.totalPages ? pagination.totalPages * pageSize : list.length));
    } catch (error: any) {
      message.error(error?.response?.data?.message || error.message || 'Failed to fetch stories');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchStories();
  }, [fetchStories]);

  const handleDelete = async (id: string) => {
    try {
      await storiesService.deleteStory(id);
      message.success('Story deleted');
      fetchStories();
    } catch (error: any) {
      message.error(error?.response?.data?.message || error.message || 'Failed to delete story');
    }
  };

  const columns = [
    {
      title: 'Media',
      key: 'media',
      width: 90,
      render: (record: Story) => {
        const thumb = getImageUrl(record.thumbnailUrl || record.mediaUrl || '');
        return (
          <div style={{ position: 'relative', width: 54, height: 72 }}>
            <Image
              src={thumb}
              alt="story"
              width={54}
              height={72}
              style={{ objectFit: 'cover', borderRadius: 6 }}
              fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='54' height='72'%3E%3Crect width='54' height='72' fill='%23222'/%3E%3C/svg%3E"
            />
            {record.mediaType === 'video' && (
              <PlayCircleOutlined
                style={{ position: 'absolute', top: 26, left: 18, color: '#fff', fontSize: 20 }}
              />
            )}
          </div>
        );
      },
    },
    {
      title: 'User',
      key: 'user',
      render: (record: Story) => {
        const u: any = record.user;
        return (
          <Space>
            <Avatar
              size="small"
              src={typeof u === 'object' && u?.profileImage ? getImageUrl(u.profileImage) : undefined}
              icon={<UserOutlined />}
            />
            <div>
              <div>{userName(u)}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {typeof u === 'object' ? u?.phone || u?.email || '' : ''}
              </Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Caption',
      dataIndex: 'caption',
      key: 'caption',
      ellipsis: true,
      render: (c?: string) => c || <Text type="secondary">—</Text>,
    },
    {
      title: 'Type',
      dataIndex: 'mediaType',
      key: 'mediaType',
      width: 90,
      render: (t: string) => <Tag color={t === 'video' ? 'volcano' : 'blue'}>{t}</Tag>,
    },
    {
      title: 'Views',
      dataIndex: 'viewsCount',
      key: 'viewsCount',
      width: 90,
      render: (v?: number) => (
        <Space size={4}>
          <EyeOutlined />
          {v ?? 0}
        </Space>
      ),
    },
    {
      title: 'Posted',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (d: string) => (d ? new Date(d).toLocaleString() : '-'),
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (record: Story) => {
        const expired = record.expiresAt ? new Date(record.expiresAt).getTime() < Date.now() : false;
        return expired ? <Tag>Expired</Tag> : <Tag color="green">Active</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (record: Story) => (
        <Popconfirm
          title="Delete this story?"
          description="This cannot be undone."
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={() => handleDelete(record._id)}
        >
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Stories
          </Title>
          <Text type="secondary">Moderate user stories — review and remove inappropriate content.</Text>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={fetchStories}>
            Refresh
          </Button>
        </Col>
      </Row>

      <Card>
        <Table
          rowKey="_id"
          loading={loading}
          columns={columns as any}
          dataSource={stories}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `${t} stories`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default Stories;
